class SplitSizePickerController {
  constructor(container) {
    this.container = container;
    const dataElement = container.querySelector('.split-size-picker__data');

    if (!dataElement) {
      return;
    }

    try {
      this.data = JSON.parse(dataElement.textContent || '{}');
    } catch (error) {
      this.data = { selected: {}, variants: [] };
    }

    this.formId = container.dataset.formId;
    this.optionPosition = Number.parseInt(container.dataset.optionPosition || '1', 10);
    this.paramName = container.dataset.paramName;
    // Back in Stock: on 'backinstock'-tagged products, allow selecting sold-out sizes.
    this.allowOos = container.dataset.allowOos === 'true';

    this.form = document.getElementById(this.formId) || container.closest('form');
    this.variantPicker = container.closest('variant-picker');
    this.inputsWrapper = container.parentElement.querySelector('.split-size-picker__inputs');
    this.radioInputs = Array.from(this.inputsWrapper?.querySelectorAll('input[data-option-position]') || []);

    this.waistGroup = container.querySelector('.split-size-picker__group[data-dimension="waist"]');
    this.lengthGroup = container.querySelector('.split-size-picker__group[data-dimension="length"]');
    this.waistButtons = Array.from(this.waistGroup?.querySelectorAll('.split-size-picker__value') || []);
    this.lengthButtons = Array.from(this.lengthGroup?.querySelectorAll('.split-size-picker__value') || []);

    this.matrix = this.buildMatrix(this.data.variants || []);

    const defaultWaist = this.normalizeValue(this.data?.selected?.waist) || this.waistButtons[0]?.dataset.dimensionValue || '';
    const defaultLength = this.normalizeValue(this.data?.selected?.length);

    this.state = {
      waist: defaultWaist,
      length: this.lengthButtons.length > 0 ? (defaultLength || this.getBestLengthForWaist(defaultWaist)) : ''
    };

    this.bindEvents();
    this.updateUI();
    this.syncVariant();
  }

  normalizeValue(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  buildMatrix(variants) {
    const matrix = new Map();

    (variants || []).forEach((variant) => {
      const waist = this.normalizeValue(variant.waist);
      const length = this.normalizeValue(variant.length);

      if (!matrix.has(waist)) {
        matrix.set(waist, new Map());
      }

      matrix.get(waist).set(length, {
        ...variant,
        waist,
        length
      });
    });

    return matrix;
  }

  bindEvents() {
    this.waistButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const value = this.normalizeValue(button.dataset.dimensionValue);

        // On BIS-tagged products, allow selecting a sold-out waist as long as it still has a
        // variant; otherwise keep the normal guard (sold-out waists are not selectable).
        const hasVariant = this.getVariantsForWaist(value).length > 0;
        const blocked = this.allowOos ? !hasVariant : button.classList.contains('is-disabled');
        if (blocked) {
          return;
        }

        if (value === this.state.waist) {
          return;
        }

        this.state.waist = value;

        if (this.lengthButtons.length > 0) {
          const bestLength = this.getBestLengthForWaist(value);
          this.state.length = bestLength || '';
        }

        this.updateUI();
        this.syncVariant();
      });
    });

    this.lengthButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const value = this.normalizeValue(button.dataset.dimensionValue);

        // On BIS-tagged products, allow selecting a sold-out length that still maps to a variant;
        // otherwise keep the normal guard.
        const variant = this.getVariant(this.state.waist, value);
        const blocked = this.allowOos ? !variant : (button.disabled || button.classList.contains('is-disabled'));
        if (blocked) {
          return;
        }

        if (value === this.state.length) {
          return;
        }

        this.state.length = value;
        this.updateUI();
        this.syncVariant();
      });
    });
  }

  getVariantsForWaist(waist) {
    const map = this.matrix.get(this.normalizeValue(waist));
    return map ? Array.from(map.values()) : [];
  }

  getVariant(waist, length) {
    const map = this.matrix.get(this.normalizeValue(waist));
    return map ? map.get(this.normalizeValue(length)) : undefined;
  }

  getBestLengthForWaist(waist) {
    const variants = this.getVariantsForWaist(waist);

    if (variants.length === 0) {
      return '';
    }

    const availableVariant = variants.find((variant) => variant.available);
    return this.normalizeValue((availableVariant || variants[0]).length);
  }

  updateUI() {
    const currentWaist = this.normalizeValue(this.state.waist);
    const currentLength = this.normalizeValue(this.state.length);

    this.waistButtons.forEach((button) => {
      const value = this.normalizeValue(button.dataset.dimensionValue);
      const variants = this.getVariantsForWaist(value);
      const hasVariant = variants.length > 0;
      const hasAvailable = variants.some((variant) => variant.available);
      const isSelected = value === currentWaist;

      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

      const shouldDisable = !hasVariant || !hasAvailable;
      button.classList.toggle('is-disabled', shouldDisable);
      button.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');
    });

    this.lengthButtons.forEach((button) => {
      const value = this.normalizeValue(button.dataset.dimensionValue);
      const variant = this.getVariant(currentWaist, value);
      const isSelected = value === currentLength;
      const isAvailable = Boolean(variant?.available);

      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

      const shouldDisable = !variant;
      button.classList.toggle('is-disabled', shouldDisable || !isAvailable);
      button.setAttribute('aria-disabled', shouldDisable || !isAvailable ? 'true' : 'false');
    });
  }

  syncVariant() {
    const targetLength = this.lengthButtons.length > 0 ? this.normalizeValue(this.state.length) : '';
    const variant = this.getVariant(this.state.waist, targetLength);

    if (!variant) {
      return;
    }

    const matchingInput = this.radioInputs.find((input) => {
      return this.normalizeValue(input.dataset.waist) === variant.waist && this.normalizeValue(input.dataset.length) === variant.length;
    });

    if (!matchingInput) {
      return;
    }

    if (!matchingInput.checked) {
      matchingInput.checked = true;
      this.updateVariantInUrl(variant);
      matchingInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // Keep the ?variant= URL param in sync with the selected split-size variant. The theme's
  // selectCombination() skips the URL update when productChange is true (combined listings with
  // data-product-url), which would leave the param stale. A correct param lets integrations that
  // read it (e.g. Klaviyo back-in-stock) target the right variant.
  updateVariantInUrl(variant) {
    if (!this.variantPicker || !this.variantPicker.hasAttribute('update-url')) {
      return;
    }

    const variantId = variant.variantId;

    if (!variantId) {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({ path: url.toString() }, '', url.toString());
  }
}

function initSplitSizePickers(scope = document) {
  const root = scope instanceof Element ? scope : document;
  const candidates = root.matches?.('.split-size-picker') ? [root] : Array.from(root.querySelectorAll('.split-size-picker'));

  candidates.forEach((container) => {
    if (container.dataset.initialized === 'true') {
      return;
    }

    container.dataset.initialized = 'true';
    new SplitSizePickerController(container);
  });
}

function observeDom() {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) {
          return;
        }

        if (node.matches('.split-size-picker')) {
          initSplitSizePickers(node);
        } else if (node.querySelector) {
          initSplitSizePickers(node);
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initSplitSizePickers();
    observeDom();
  });
} else {
  initSplitSizePickers();
  observeDom();
}

document.addEventListener('product:rerender', (event) => {
  requestAnimationFrame(() => {
    const target = event.target instanceof HTMLElement ? event.target : document;
    initSplitSizePickers(target);
  });
});

document.addEventListener('shopify:section:load', (event) => {
  initSplitSizePickers(event.target);
});

